import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const STORAGE_KEY = '@budget/pending_household_invite_token';
const SESSION_KEY = 'budget_pending_household_invite_token';

type WebSession = {
  sessionStorage?: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
  };
};

function webSession(): WebSession['sessionStorage'] | null {
  if (Platform.OS !== 'web') return null;
  try {
    return (globalThis as WebSession).sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Persist invite token across auth overlays / OAuth / remounts. */
export async function savePendingInviteToken(token: string): Promise<void> {
  const trimmed = token.trim();
  if (!trimmed) return;
  const session = webSession();
  session?.setItem(SESSION_KEY, trimmed);
  try {
    await AsyncStorage.setItem(STORAGE_KEY, trimmed);
  } catch {
    // ignore
  }
}

export async function loadPendingInviteToken(): Promise<string | null> {
  const session = webSession();
  const fromSession = session?.getItem(SESSION_KEY)?.trim();
  if (fromSession) return fromSession;
  try {
    const fromAsync = (await AsyncStorage.getItem(STORAGE_KEY))?.trim();
    return fromAsync || null;
  } catch {
    return null;
  }
}

export async function clearPendingInviteToken(): Promise<void> {
  const session = webSession();
  session?.removeItem(SESSION_KEY);
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

const DISMISSED_KEY = '@budget/dismissed_household_invite_tokens';
const MAX_DISMISSED = 20;

async function loadDismissedInviteTokens(): Promise<string[]> {
  try {
    const raw = await AsyncStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()));
  } catch {
    return [];
  }
}

/** Remember that the user tapped Not now so we don't re-prompt on the next login. */
export async function rememberDismissedInviteToken(token: string): Promise<void> {
  const trimmed = token.trim();
  if (!trimmed) return;
  const existing = await loadDismissedInviteTokens();
  const next = [trimmed, ...existing.filter((item) => item !== trimmed)].slice(0, MAX_DISMISSED);
  try {
    await AsyncStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

export async function isInviteTokenDismissed(token: string): Promise<boolean> {
  const trimmed = token.trim();
  if (!trimmed) return false;
  const existing = await loadDismissedInviteTokens();
  return existing.includes(trimmed);
}
